import React, {useState} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import cx from 'classnames';
import {issueAttributesType} from '../../constants/proptype-validation';
import Button from '@adsk/bim360-matrix-react-button';
import FormField from '../common/FormField';
import {viewTypes} from '../../constants/view-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {hasPermittedAttribute} from '../../utils/issues-helpers';
import useCurrentIssuePreviewPopup from './useCurrentIssuePreviewPopup';
import {getActiveIssueSubTypesGroupedDropdownOptions, getCurrentViewType} from '../../app/selectors';
import {connect} from '../../app/connect';
import {updateIssueAction} from '../../app/actions';
import SelectBoxAdapter from '../common/SelectBoxAdapter';

const IssuePreviewNoTypeStatusChangePopup = ({
  issue,
  dispatch,
  viewType,
  activeIssueSubTypesGroupedDropdownOptions
}) => {
  const dropdownMaxWidth = 348;
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSubType, setSelectedSubType] = useState(null);
  const {setCurrentIssuePreviewPopup} = useCurrentIssuePreviewPopup();
  const divClassNames = cx('IssuePreviewNoTypeStatusChangePopup', {
    'IssuePreviewNoTypeStatusChangePopup--narrow': viewType === viewTypes.DOCUMENT,
    'IssuePreviewNoTypeStatusChangePopup--wide': viewType === viewTypes.PROJECT
  });

  const canEditType = hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID);
  if (!canEditType) {
    return (
      <div className={divClassNames}>
        <div className="IssuePreviewNoTypeStatusChangePopup__content">
          <div className="IssuePreviewNoTypeStatusChangePopup__message">
            {i18n.t('preview.missingType.contactAdmin')}
          </div>
        </div>
        <div className="IssuePreviewNoTypeStatusChangePopup__footer">
          <Button
            styleType={Button.StyleType.PRIMARY}
            onClick={() => setCurrentIssuePreviewPopup(null)}
            data-testid="IssuePreviewNoTypeStatusChangePopup__got-it-button"
          >
            {i18n.t('navigation.gotIt')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={divClassNames}>
      <div className="IssuePreviewNoTypeStatusChangePopup__content">
        <div className="IssuePreviewNoTypeStatusChangePopup__edit-message">{i18n.t('preview.missingType.addType')}</div>
        <FormField
          labelText={i18n.t('new.issueTypeAndSubType')}
          data-testid="IssuePreviewNoTypeStatusChangePopup__type-field"
        >
          <SelectBoxAdapter
            selectedOptions={selectedSubType}
            labelKey="title"
            valueKey="id"
            options={activeIssueSubTypesGroupedDropdownOptions}
            grouped
            onChange={setSelectedSubType}
            clearable={false}
            searchable
            searchIcon
            searchPlaceholder={i18n.t('new.search')}
            dropdownMaxWidth={dropdownMaxWidth}
          />
        </FormField>
      </div>

      <div className="IssuePreviewNoTypeStatusChangePopup__footer">
        <Button
          styleType={Button.StyleType.LINK_SECONDARY}
          data-testid="IssuePreviewNoTypeStatusChangePopup__cancel-button"
          onClick={() => setCurrentIssuePreviewPopup(null)}
          disabled={isSaving}
        >
          {i18n.t('navigation.cancel')}
        </Button>
        <Button
          styleType={Button.StyleType.PRIMARY}
          className={cx('IssuePreviewNoTypeStatusChangePopup__save-button', {
            ['IssuePreviewNoTypeStatusChangePopup__save-button--saving']: isSaving
          })}
          data-testid="IssuePreviewNoTypeStatusChangePopup__saveButton"
          onClick={() => {
            setIsSaving(true);
            dispatch(
              updateIssueAction(
                issue,
                {
                  [ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]: selectedSubType.parentId,
                  [ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: selectedSubType.id
                },
                {
                  onError: () => {
                    setIsSaving(false);
                  },
                  onSuccess: () => {
                    setIsSaving(false);
                    setCurrentIssuePreviewPopup(null);
                  }
                }
              )
            );
          }}
          disabled={isSaving || !selectedSubType}
        >
          {isSaving ? i18n.t('navigation.updating') : i18n.t('navigation.done')}
        </Button>
      </div>
    </div>
  );
};

IssuePreviewNoTypeStatusChangePopup.displayName = 'IssuePreviewNoTypeStatusChangePopup';

IssuePreviewNoTypeStatusChangePopup.propTypes = {
  issue: issueAttributesType,
  dispatch: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  activeIssueSubTypesGroupedDropdownOptions: PropTypes.array
};

const mapStateToProps = state => ({
  activeIssueSubTypesGroupedDropdownOptions: getActiveIssueSubTypesGroupedDropdownOptions(state),
  viewType: getCurrentViewType(state)
});

export default connect(mapStateToProps)(IssuePreviewNoTypeStatusChangePopup);
